import { z } from 'zod';
import { evaluateAmountInput, INVALID_AMOUNT_MESSAGE, isValidSubmittedAmount, SUPPORT_AMOUNT_MAX } from '../supportAmount';
import { TFunction } from 'i18next';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import GoalWithLimitsDtoIn from '@/models/in/GoalWithLimitsDtoIn';
import { PaymentStartTiming } from '@/models/common/PaymentStartTiming';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { isBirthDateInAllowedRange, MIN_PLAUSIBLE_YEAR } from '@/helpers/dateHelper';
import { requiresDocumentDate } from '@/helpers/supportDocumentDate';

// Must match the [InvoiceNumber] nvarchar(50) column on SupportRequests (see
// AddInvoiceNumberToSupportRequests.sql, enforced there too via a CHECK constraint).
export const INVOICE_NUMBER_MAX_LENGTH = 50;

/** Amount rules — separate refine so they always run even when other fields fail. */
export function appendAmountValidationIssues(
  data: {
    amount: number;
    amountInputInvalid: boolean;
    amountRawInput: string | null;
  },
  ctx: z.RefinementCtx
) {
  if (data.amountRawInput !== null && data.amountRawInput !== '') {
    const evaluated = evaluateAmountInput(data.amountRawInput);
    if (evaluated.error) {
      ctx.addIssue({
        code: 'custom',
        message: INVALID_AMOUNT_MESSAGE,
        path: ['amount'],
      });
      return;
    }
  } else if (data.amountInputInvalid) {
    ctx.addIssue({
      code: 'custom',
      message: INVALID_AMOUNT_MESSAGE,
      path: ['amount'],
    });
    return;
  }

  if (!Number.isFinite(data.amount) || data.amount === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'יש להזין סכום',
      path: ['amount'],
    });
  } else if (data.amount < 0 || data.amount > SUPPORT_AMOUNT_MAX || !isValidSubmittedAmount(data.amount)) {
    ctx.addIssue({
      code: 'custom',
      message: INVALID_AMOUNT_MESSAGE,
      path: ['amount'],
    });
  }
}

/**
 * HAT-1259. These must stay in step with the C# ThirdPartyPaymentReason enum - the API serializes
 * enums by name. The displayed Hebrew is expected to change ("אחרי זה נחדד ונשנה טקסטים"); these
 * values are not.
 */
export const THIRD_PARTY_REASONS = ['SeizingBody', 'ExecutionOffice', 'WelfareRecommendation', 'Other'] as const;

/** Mirrors the C# ThirdPartyPayeeRelationship enum. The office has yet to confirm this list. */
export const THIRD_PARTY_RELATIONSHIPS = ['Spouse', 'FamilyMember', 'Guardian', 'Lawyer', 'PublicBody', 'ServiceProvider', 'Other'] as const;

// Document with metadata
const documentWithMetadataSchema = z.object({
  file: z.instanceof(File, { error: 'הקובץ שצורף אינו תקין' }),
  requiredDocName: z.string(),
});

const additionalDocumentSchema = z.object({
  file: z.custom<File | null>(),
  customDescription: z.string(),
});

// New child data schema factory - creates schema with translated error messages
export const createNewChildSchema = (t: TFunction) =>
  z.object({
    firstName: z.string().min(1, t('pleaseEnterFirstName')),
    israeliId: z.string().min(1, t('pleaseEnterId')).refine(isValidIsraeliID, t('invalidIsraeliId')),
    birthDate: z
      .string()
      .nullable()
      .refine((val) => val !== null && val.length > 0, t('pleaseEnterBirthDate'))
      .refine(isBirthDateInAllowedRange, t('birthDateTooEarly', { year: MIN_PLAUSIBLE_YEAR })),
    livesWithSupported: z.boolean(),
    idAppendixFile: z.custom<File | null>().refine((file) => file !== null, t('pleaseUploadIdAppendix')),
  });

// Type inferred from schema
export type NewChildData = z.infer<ReturnType<typeof createNewChildSchema>>;

export const supportRequestFormSchema = z
  .object({
    supported: z.custom<ContactDtoIn>().nullable(),
    goalWithLimits: z
      .custom<GoalWithLimitsDtoIn>()
      .nullable()
      .refine((val) => val !== null, 'יש לבחור מטרת תמיכה'),
    supportRecipient: z.string().nullable(),
    childId: z.number({ error: 'יש לבחור ילד/ה' }).nullable(),
    weddingDate: z.string().nullable(),
    // SPR-10015: date of the supporting document, for the goals that require one (not Wedding).
    documentDate: z.string().nullable().default(null),
    // Kollel-specific fields
    kollelName: z.string().optional(),
    studyTimes: z.array(z.string()).default([]),
    kollelAddress: z.string().optional(),
    studyMaterial: z.string().optional(),
    roshKollelName: z.string().optional(),
    documents: z.array(documentWithMetadataSchema),
    additionalDocuments: z.array(additionalDocumentSchema).default([]),
    // HAT-1109: הבדיקות ברמת השדה כאן רצות לפני ה-superRefine ועוצרות אותו, ולכן כל
    // אחת מהן חייבת הודעה עברית משלה - אחרת המשתמש מקבל את ברירת המחדל של זוד
    // במקום ההודעה המנוסחת שלמטה.
    multiRequestTotalCount: z
      .number({ error: 'יש לבחור מספר חודשי תשלום' })
      .min(1, 'מספר חודשי התשלום חייב להיות לפחות 1')
      .max(12, 'מספר חודשי התשלום יכול להיות עד 12')
      .nullable(),
    paymentStartTiming: z.enum(PaymentStartTiming, { error: 'יש לבחור מועד התחלת תשלומים' }).default(PaymentStartTiming.Immediate),
    paymentStartMonth: z.string().nullable(),
    transferDayOfMonth: z.union([z.literal(1), z.literal(15)], { error: 'יש לבחור יום העברה בחודש - 1 או 15' }).default(1),
    paymentInfoId: z.number({ error: 'יש לבחור אופן קבלת התשלום' }).default(0),
    /**
     * Selected SupplierPaymentInfo id when the user picks a previously-used bank account for the
     * supplier (only relevant for fund-expense / supplier requests). 0 (the default) means "use a
     * NEW bank account" - the bankCode/bankBranch/bankAccount inputs then carry the new details.
     */
    supplierPaymentInfoId: z.number({ error: 'יש לבחור חשבון בנק של הספק' }).default(0),
    /** Set by SupportAmountInput when the typed text fails parse (comma rule, etc.). */
    amountInputInvalid: z.boolean().default(false),
    /** In-progress amount text; not sent to the API. null = show formatted `amount`. */
    amountRawInput: z.string().nullable().default(null),
    amount: z.number({ error: 'יש להזין סכום' }),
    userComments: z.string().optional(),
    exceedsBalanceComment: z.string().optional(),
    exceedsAvailableBalance: z.boolean().default(false),
    exceedsEligibleAmount: z.boolean().default(false),
    exceptionsCommitteeAcknowledged: z.boolean().default(false),
    // Fund expense fields
    isAlreadyPaid: z.boolean().default(false),
    selectedSupplierId: z.number({ error: 'יש לבחור ספק' }).nullable().default(null),
    /**
     * Optional link to one of the fund's APPROVED projects, so a fund expense can be booked
     * against a project's budget. Only offered on the fund-expense path; null everywhere else.
     */
    projectId: z.number({ error: 'יש לבחור מיזם תקין' }).nullable().default(null),
    supplierName: z.string().nullable(),
    supplierTaxId: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    /** Set from a debounced real-time server check while the user types (see FundPaymentSetup). */
    isDuplicateInvoiceNumber: z.boolean().default(false),
    bankCode: z.number({ error: 'יש להזין קוד בנק' }).nullable(),
    bankAccount: z.string().nullable(),
    bankBranch: z.string().nullable(),
    payerName: z.string().nullable(),
    payerIsraeliId: z.string().nullable(),
    /**
     * HAT-1259 - the money goes to someone other than the beneficiary. Only offered when the fund
     * holds an active authorization for this beneficiary; the server re-checks it on submit, so a
     * form that offers the section is not what permits the payment.
     */
    isThirdPartyPayment: z.boolean().default(false),
    /** A payee already saved on this fund. 0 means "new payee", and the fields below carry it. */
    thirdPartyPayeeId: z.number().nullable().default(null),
    thirdPartyReason: z.enum(THIRD_PARTY_REASONS).nullable().default(null),
    thirdPartyReasonDetails: z.string().nullable(),
    thirdPartyPayeeName: z.string().nullable(),
    thirdPartyPayeeIsraeliId: z.string().nullable(),
    thirdPartyRelationship: z.enum(THIRD_PARTY_RELATIONSHIPS).nullable().default(null),
    thirdPartyBankCode: z.number().nullable(),
    thirdPartyBankBranch: z.string().nullable(),
    thirdPartyBankAccount: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    appendAmountValidationIssues(data, ctx);
  })
  .superRefine((data, ctx) => {
    // Child selection (Step 2 - RequiredDetails)
    const goal = data.goalWithLimits?.goal;

    // Validate supported only if goal is not Fund
    if (goal?.allowedFor !== 'Fund' && !data.supported) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש לבחור נתמך',
        path: ['supported'],
      });
    }

    // Validate paymentInfoId only if goal is not Fund
    if (goal?.allowedFor !== 'Fund') {
      if (!data.paymentInfoId || data.paymentInfoId === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור אופן קבלת התשלום',
          path: ['paymentInfoId'],
        });
      } else if (data.paymentInfoId < 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'אופן קבלת התשלום לא תקין',
          path: ['paymentInfoId'],
        });
      }
    }

    // Validate Fund expense fields
    if (goal?.allowedFor === 'Fund') {
      if (!data.supplierName || data.supplierName.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין שם ספק',
          path: ['supplierName'],
        });
      }

      if (!data.supplierTaxId || data.supplierTaxId.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין מספר זהות/ח.פ של ספק',
          path: ['supplierTaxId'],
        });
      }

      if (!data.invoiceNumber || data.invoiceNumber.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין מספר חשבונית',
          path: ['invoiceNumber'],
        });
      } else if (data.invoiceNumber.trim().length > INVOICE_NUMBER_MAX_LENGTH) {
        ctx.addIssue({
          code: 'custom',
          message: `מספר חשבונית יכול להכיל עד ${INVOICE_NUMBER_MAX_LENGTH} תווים`,
          path: ['invoiceNumber'],
        });
      } else if (data.isDuplicateInvoiceNumber) {
        ctx.addIssue({
          code: 'custom',
          message: 'מספר חשבונית זה כבר קיים עבור ספק זה',
          path: ['invoiceNumber'],
        });
      }

      // Bank-detail validation rules for the fund-expense flow:
      //  - Already-paid (reimbursement): the bank fields belong to the payer; always required.
      //  - Paying the vendor directly with supplierPaymentInfoId > 0: the user picked a stored
      //    account, bank fields come from there and aren't validated against the form.
      //  - Paying the vendor directly with supplierPaymentInfoId === 0: the user is entering a NEW
      //    bank account; require the three bank fields.
      const requiresInlineBankFields = data.isAlreadyPaid || !data.supplierPaymentInfoId;
      if (requiresInlineBankFields) {
        if (!data.bankCode || data.bankCode === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין קוד בנק',
            path: ['bankCode'],
          });
        }

        if (!data.bankAccount || data.bankAccount.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מספר חשבון בנק',
            path: ['bankAccount'],
          });
        }

        if (!data.bankBranch || data.bankBranch.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מספר סניף',
            path: ['bankBranch'],
          });
        }
      }

      // Validate payer details if already paid
      if (data.isAlreadyPaid) {
        if (!data.payerName || data.payerName.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין שם מבצע התשלום',
            path: ['payerName'],
          });
        }

        if (!data.payerIsraeliId || data.payerIsraeliId.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מספר זהות של מבצע התשלום',
            path: ['payerIsraeliId'],
          });
        } else if (!isValidIsraeliID(data.payerIsraeliId)) {
          ctx.addIssue({
            code: 'custom',
            message: 'תעודת זהות לא תקינה',
            path: ['payerIsraeliId'],
          });
        }
      }
    }

    // HAT-1259 - payee details for a third-party payment. Only on the beneficiary path: a fund
    // expense already pays someone who is not a beneficiary, through the supplier flow.
    if (data.isThirdPartyPayment && goal?.allowedFor !== 'Fund') {
      if (!data.thirdPartyReason) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור סיבת בקשה לתשלום לצד ג׳',
          path: ['thirdPartyReason'],
        });
      } else if (data.thirdPartyReason === 'Other' && !data.thirdPartyReasonDetails?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לפרט את סיבת הבקשה',
          path: ['thirdPartyReasonDetails'],
        });
      }

      // A stored payee carries its own details; only a new one is validated against the form.
      if (!data.thirdPartyPayeeId) {
        if (!data.thirdPartyPayeeName?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין שם מוטב',
            path: ['thirdPartyPayeeName'],
          });
        }

        // Mandatory, per Moshe Klein 2026-09-23 answer 4, even though the form's placeholder
        // ("מס' ת.ז. מוטב חלופי") reads like an optional field.
        //
        // Checked for shape only, not with isValidIsraeliID: a payee is usually a body and carries
        // a ח.פ. or a government office number, which does not satisfy the ת.ז. check digit. HAT-1341
        // is the reminder of what rejecting a legitimate number costs.
        if (!data.thirdPartyPayeeIsraeliId?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מזהה מוטב',
            path: ['thirdPartyPayeeIsraeliId'],
          });
        } else if (!/^\d{5,15}$/.test(data.thirdPartyPayeeIsraeliId.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: 'מזהה המוטב חייב להכיל בין 5 ל-15 ספרות',
            path: ['thirdPartyPayeeIsraeliId'],
          });
        }

        if (!data.thirdPartyRelationship) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש לבחור קשר לנתמך',
            path: ['thirdPartyRelationship'],
          });
        }

        if (!data.thirdPartyBankCode) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש לבחור קוד בנק של המוטב',
            path: ['thirdPartyBankCode'],
          });
        }

        if (!data.thirdPartyBankBranch?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מספר סניף של המוטב',
            path: ['thirdPartyBankBranch'],
          });
        }

        if (!data.thirdPartyBankAccount?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש להזין מספר חשבון בנק של המוטב',
            path: ['thirdPartyBankAccount'],
          });
        }
      }
    }

    if (goal) {
      const shouldShowChildSelection =
        goal.allowedFor === 'SupportedChildren' || (goal.allowedFor === 'SupportedAndSupportedChildren' && data.supportRecipient === 'SupportedChildren');

      // User must select an existing child (new children are saved separately before submission)
      if (shouldShowChildSelection && !data.childId) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור ילד/ה',
          path: ['childId'],
        });
      }

      // Required documents (Step 2 - RequiredDetails)
      // User must upload at least one of the required documents
      if (goal.requiredDocuments && goal.requiredDocuments.length > 0) {
        const uploadedRequiredDocs = data.documents.filter((doc) => goal.requiredDocuments.includes(doc.requiredDocName));

        if (uploadedRequiredDocs.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: `יש להעלות לפחות אחד מהמסמכים הנדרשים: ${goal.requiredDocuments.join(', ')}`,
            path: ['documents'],
          });
        }
      }
    }

    // Wedding date required for Wedding goal (Step 2 - RequiredDetails)
    if (goal?.name === 'Wedding' && !data.weddingDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להזין תאריך חתונה',
        path: ['weddingDate'],
      });
    }

    if (requiresDocumentDate(goal) && !data.documentDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להזין את תאריך האסמכתא',
        path: ['documentDate'],
      });
    }

    // Kollel-specific fields validation (Step 2 - RequiredDetails)
    if (goal?.name === 'Kollel') {
      if (!data.kollelName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין שם הכולל',
          path: ['kollelName'],
        });
      }
      if (!data.studyTimes || data.studyTimes.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור לפחות זמן לימוד אחד',
          path: ['studyTimes'],
        });
      }
      if (!data.kollelAddress?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין כתובת הכולל',
          path: ['kollelAddress'],
        });
      }
      if (!data.studyMaterial?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין חומר הלימוד',
          path: ['studyMaterial'],
        });
      }
      if (!data.roshKollelName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להזין שם ראש הכולל',
          path: ['roshKollelName'],
        });
      }
    }

    // User comments required for UnusualExpense goal (Step 4 - UserComments)
    if (goal?.name === 'UnusualExpense' && (!data.userComments || data.userComments.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להזין פרטים על ההוצאה החריגה',
        path: ['userComments'],
      });
    }

    // Multi-month payment fields (Step 3 - PaymentSetup)
    const goalEnablesMultipleMonths = goal?.name === 'MonthlySupport' || goal?.name === 'Kollel';
    if (goalEnablesMultipleMonths) {
      if (!data.multiRequestTotalCount || data.multiRequestTotalCount < 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור מספר חודשי תשלום',
          path: ['multiRequestTotalCount'],
        });
      }
      // Date-related fields validation only when multiple months selected
      if (data.multiRequestTotalCount && data.multiRequestTotalCount > 1) {
        if (!data.paymentStartTiming) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש לבחור מועד התחלת תשלומים',
            path: ['paymentStartTiming'],
          });
        }
        if (!data.transferDayOfMonth) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש לבחור יום העברה בחודש',
            path: ['transferDayOfMonth'],
          });
        }
        if (data.paymentStartTiming === PaymentStartTiming.SpecificMonth && !data.paymentStartMonth) {
          ctx.addIssue({
            code: 'custom',
            message: 'יש לבחור חודש התחלה',
            path: ['paymentStartMonth'],
          });
        }
      }
    }

    // Exceptions committee acknowledgement required when amount exceeds available balance or eligible amount (Step 3 - PaymentSetup)
    if ((data.exceedsAvailableBalance || data.exceedsEligibleAmount) && !data.exceptionsCommitteeAcknowledged) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש לאשר את ההעברה לוועדת חריגים',
        path: ['exceptionsCommitteeAcknowledged'],
      });
    }

    // Exceeds balance comment required when amount exceeds available balance or eligible amount (Step 3 - PaymentSetup)
    if ((data.exceedsAvailableBalance || data.exceedsEligibleAmount) && (!data.exceedsBalanceComment || data.exceedsBalanceComment.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להזין סיבת חריגה מהיתרה',
        path: ['exceedsBalanceComment'],
      });
    }
  })
  .superRefine((data, ctx) => {
    appendAmountValidationIssues(data, ctx);
  });

export type SupportRequestFormData = z.infer<typeof supportRequestFormSchema>;

export const defaultSupportRequestFormValues: SupportRequestFormData = {
  supported: null,
  goalWithLimits: null,
  supportRecipient: null,
  childId: null,
  weddingDate: null,
  documentDate: null,
  // Kollel-specific fields
  kollelName: '',
  studyTimes: [],
  kollelAddress: '',
  studyMaterial: '',
  roshKollelName: '',
  documents: [],
  additionalDocuments: [],
  multiRequestTotalCount: null,
  paymentStartTiming: PaymentStartTiming.Immediate,
  paymentStartMonth: null,
  transferDayOfMonth: 1,
  paymentInfoId: 0,
  supplierPaymentInfoId: 0,
  amountInputInvalid: false,
  amountRawInput: null,
  amount: 0,
  userComments: '',
  exceedsBalanceComment: '',
  exceedsAvailableBalance: false,
  exceedsEligibleAmount: false,
  exceptionsCommitteeAcknowledged: false,
  // Fund expense fields
  isAlreadyPaid: false,
  selectedSupplierId: null,
  projectId: null,
  supplierName: null,
  supplierTaxId: null,
  invoiceNumber: null,
  isDuplicateInvoiceNumber: false,
  bankCode: null,
  bankAccount: null,
  bankBranch: null,
  payerName: null,
  payerIsraeliId: null,
  isThirdPartyPayment: false,
  thirdPartyPayeeId: null,
  thirdPartyReason: null,
  thirdPartyReasonDetails: null,
  thirdPartyPayeeName: null,
  thirdPartyPayeeIsraeliId: null,
  thirdPartyRelationship: null,
  thirdPartyBankCode: null,
  thirdPartyBankBranch: null,
  thirdPartyBankAccount: null,
};
